import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Replaces [key] (or {{ key }}) placeholders in a translated string.
 * @param {string} template
 * @param {Record<string, string | number>} vars
 * @returns {string}
 */
const fill = (template, vars = {}) =>
  String(template ?? '').replace(/{{\s*(\w+)\s*}}|\[(\w+)\]|%\{(\w+)\}/g, (match, a, b, c) => {
    const key = a || b || c;
    if (!(key in vars)) return match;
    // Turkish strings write the percent sign before the number ("%{percent}" -> "%10").
    return (c ? '%' : '') + String(vars[key]);
  });

/**
 * Derives a Shopify money format template from a server-rendered sample of 123456 minor units,
 * so client-side totals match the `money` filter output of the current market/currency.
 * @param {string} sample
 * @returns {string}
 */
function deriveMoneyFormat(sample) {
  const match = /\d[\d.,'\s]*\d|\d/.exec(sample || '');
  if (!match) return '{{amount}}';
  const number = match[0];
  const prefix = sample.slice(0, match.index);
  const suffix = sample.slice(match.index + number.length);
  const hasDecimals = /[.,]\d{2}$/.test(number);
  const integerPart = hasDecimals ? number.slice(0, -3) : number;
  const decimalSeparator = hasDecimals ? number.slice(-3, -2) : '';
  const thousands = (/[^\d]/.exec(integerPart) || [''])[0];
  let token = 'amount';
  if (hasDecimals) {
    if (decimalSeparator === ',' && /\s/.test(thousands)) token = 'amount_with_space_separator';
    else if (decimalSeparator === ',') token = 'amount_with_comma_separator';
    else if (/\s/.test(thousands)) token = 'amount_with_period_and_space_separator';
    else if (thousands === "'") token = 'amount_with_apostrophe_separator';
  } else if (thousands === '.') {
    token = 'amount_no_decimals_with_comma_separator';
  } else if (/\s/.test(thousands)) {
    token = 'amount_no_decimals_with_space_separator';
  } else {
    token = 'amount_no_decimals';
  }
  return `${prefix}{{${token}}}${suffix}`;
}

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Formmen product page controller: gallery, lightbox, quantity tiers, per-item option rows,
 * add to cart (Horizon cart events), mobile sticky bar and rule-based size finder.
 */
class FmProduct extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {any} */
  #data = null;
  /** @type {Array<Array<string | null>>} */
  #rows = [];
  /** @type {Array<{quantity: number, discount: number, label: string, badge: string, isDefault: boolean}>} */
  #tiers = [];
  #tierIndex = 0;
  #busy = false;
  #tried = false;
  #moneyFormat = '{{amount}}';
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #visibility = { submit: true, submitAbove: false, footer: false };
  #slideIndex = 0;
  #scrollFrame = 0;
  #colorOptionIndex = -1;
  #multiplier = 1;
  #sizeOptionIndex = -1;
  #recommended = '';
  #resetLabelTimer = 0;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const json = this.querySelector('[data-product-json]');
    if (!json) return;
    try {
      this.#data = JSON.parse(json.textContent || '{}');
    } catch (error) {
      console.error('[fm-product] Invalid product JSON', error);
      return;
    }

    const data = this.#data;
    this.#moneyFormat = deriveMoneyFormat(data.moneySample);
    this.#colorOptionIndex = data.options.findIndex((/** @type {any} */ option) => option.isColor);
    this.#setupTiers();
    this.#setupRows();
    this.#setupGallery(signal);
    this.#setupForm(signal);
    this.#setupQuantity(signal);
    this.#setupSticky(signal);
    this.#setupSizeGuide(signal);

    document.addEventListener('fm:select-tier', this.#onExternalTier, { signal });
    document.addEventListener('fm:open-size-guide', this.#onExternalSizeGuide, { signal });

    this.#render();
    this.#applyMediaFilter(false);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#observer?.disconnect();
    this.#observer = null;
    cancelAnimationFrame(this.#scrollFrame);
    clearTimeout(this.#resetLabelTimer);
  }

  /* ---------------------------------------------------------------- tiers */

  #setupTiers() {
    const tiers = [...(this.#data.tiers || [])]
      .filter((tier) => Number(tier.quantity) > 0)
      .sort((a, b) => a.quantity - b.quantity);
    this.#tiers = tiers.length ? tiers : [{ quantity: 1, discount: 0, label: '', badge: '', isDefault: true }];

    const params = new URLSearchParams(window.location.search);
    const requested = Number(params.get('adet'));
    let index = this.#tiers.findIndex((tier) => tier.quantity === requested);
    if (index < 0) index = this.#tiers.findIndex((tier) => tier.isDefault);
    this.#tierIndex = Math.max(0, index);
  }

  get #tier() {
    return this.#tiers[this.#tierIndex] || this.#tiers[0];
  }

  /** @param {number} index */
  #selectTier(index) {
    if (index < 0 || index >= this.#tiers.length || index === this.#tierIndex) return;
    this.#tierIndex = index;
    this.#syncRowCount();
    this.#tried = false;
    this.#render();
  }

  /** @param {Event} event */
  #onExternalTier = (event) => {
    const quantity = Number(/** @type {CustomEvent} */ (event).detail?.quantity);
    const index = this.#tiers.findIndex((tier) => tier.quantity === quantity);
    if (index < 0) return;
    this.#selectTier(index);
    const target = this.querySelector('[data-buy]');
    target?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    /** @type {HTMLElement | null} */ (this.querySelector('.fm-tier[aria-checked="true"]'))?.focus({ preventScroll: true });
  };

  /* ----------------------------------------------------------------- rows */

  #setupRows() {
    const data = this.#data;
    const selected = data.variants.find((/** @type {any} */ v) => v.id === data.selectedVariantId) || null;
    const base = data.variants.find((/** @type {any} */ v) => v.id === data.initialVariantId) || data.variants[0];

    /** @type {Array<string | null>} */
    const first = data.options.map((/** @type {any} */ option, /** @type {number} */ i) => {
      if (data.hasOnlyDefaultVariant) return base.options[i];
      if (selected) return selected.options[i];
      if (option.isColor || option.values.length === 1) return base?.options[i] ?? null;
      return null;
    });
    // ?color=<value handle> preselects a color (e.g. from a homepage card) without choosing a size.
    const wanted = new URLSearchParams(window.location.search).get('color');
    if (wanted && !selected && this.#colorOptionIndex >= 0) {
      const colorOption = data.options[this.#colorOptionIndex];
      const match = colorOption.values.find((/** @type {any} */ value) => value.handle === wanted.toLowerCase());
      if (match) first[this.#colorOptionIndex] = match.name;
    }
    this.#rows = [first];
    this.#syncRowCount();
  }

  #syncRowCount() {
    const quantity = this.#tier.quantity;
    if (this.#data.hasOnlyDefaultVariant || !this.querySelector('[data-rows]')) {
      const only = this.#rows[0];
      this.#rows = Array.from({ length: quantity }, () => [...only]);
      return;
    }
    this.#rows = this.#rows.slice(0, quantity);
    while (this.#rows.length < quantity) {
      this.#rows.push([...(this.#rows[this.#rows.length - 1] || [])]);
    }
  }

  /**
   * @param {Array<string | null>} selection
   * @returns {any | null}
   */
  #findVariant(selection) {
    if (selection.some((value) => value === null)) return null;
    return (
      this.#data.variants.find((/** @type {any} */ v) =>
        v.options.every((/** @type {string} */ value, /** @type {number} */ i) => value === selection[i])
      ) || null
    );
  }

  /**
   * Cascading availability: option i is evaluated against the selections of options before it.
   * @param {Array<string | null>} selection
   * @param {number} optionIndex
   * @param {string} value
   * @returns {{exists: boolean, available: boolean}}
   */
  #valueState(selection, optionIndex, value) {
    let exists = false;
    let available = false;
    for (const variant of this.#data.variants) {
      if (variant.options[optionIndex] !== value) continue;
      let matches = true;
      for (let j = 0; j < optionIndex; j += 1) {
        if (selection[j] !== null && variant.options[j] !== selection[j]) {
          matches = false;
          break;
        }
      }
      if (!matches) continue;
      exists = true;
      if (variant.available) {
        available = true;
        break;
      }
    }
    return { exists, available };
  }

  /**
   * @param {number} rowIndex
   * @param {number} optionIndex
   * @param {string} value
   */
  #select(rowIndex, optionIndex, value) {
    const row = this.#rows[rowIndex];
    if (!row) return;
    const state = this.#valueState(row, optionIndex, value);
    if (!state.exists || !state.available) return;
    row[optionIndex] = value;
    for (let j = optionIndex + 1; j < row.length; j += 1) {
      const current = row[j];
      if (current !== null) {
        const next = this.#valueState(row, j, current);
        if (!next.available) row[j] = null;
      }
    }
    this.#render();
    if (rowIndex === 0) {
      this.#syncUrl();
      const filtering = optionIndex === this.#colorOptionIndex && this.dataset.filterColor === 'true';
      if (filtering) this.#applyMediaFilter(true);
      const variant = this.#findVariant(row);
      // With the color filter on, the selected color's first image already leads the gallery.
      if (variant?.mediaId && !filtering) this.#goToMedia(variant.mediaId);
    }
  }

  #syncUrl() {
    const variant = this.#findVariant(this.#rows[0] || []);
    if (!variant || window.Shopify?.designMode) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    url.searchParams.delete('color');
    window.history.replaceState(window.history.state, '', url.toString());
  }

  /* --------------------------------------------------------------- render */

  #render() {
    this.#renderTiers();
    this.#renderRows();
    this.#renderTotals();
  }

  #renderTiers() {
    const container = this.querySelector('[data-tiers]');
    if (!container) return;
    const s = this.#data.strings;
    const focused = document.activeElement?.closest?.('.fm-tier') ? this.#tierIndex : -1;
    container.setAttribute('role', 'radiogroup');
    container.replaceChildren(
      ...this.#tiers.map((tier, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fm-tier';
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', String(index === this.#tierIndex));
        button.tabIndex = index === this.#tierIndex ? 0 : -1;
        button.dataset.tier = String(index);

        if (tier.badge) {
          const badge = document.createElement('span');
          badge.className = 'fm-tier__badge';
          badge.textContent = tier.badge;
          button.append(badge);
        }
        const label = document.createElement('span');
        label.className = 'fm-tier__label';
        label.textContent = tier.label || fill(s.tierQty, { count: tier.quantity });
        const sub = document.createElement('span');
        sub.className = 'fm-tier__sub';
        if (tier.discount > 0) sub.textContent = fill(s.tierDiscount, { percent: tier.discount });
        else if (tier.quantity === 1) sub.textContent = s.tierSingle;
        button.append(label, sub);
        return button;
      })
    );
    if (focused >= 0) /** @type {HTMLElement | null} */ (container.querySelector(`[data-tier="${focused}"]`))?.focus();
  }

  #renderRows() {
    const container = this.querySelector('[data-rows]');
    if (!container) return;
    const s = this.#data.strings;
    const options = this.#data.options;
    const active = /** @type {HTMLElement | null} */ (document.activeElement);
    const focusKey = active?.dataset?.focusKey && container.contains(active) ? active.dataset.focusKey : null;
    const multiple = this.#rows.length > 1;

    container.replaceChildren(
      ...this.#rows.map((row, rowIndex) => {
        const complete = !row.some((value) => value === null);
        const invalid = this.#tried && !complete;
        const card = document.createElement('div');
        card.className = `fm-item${invalid ? ' is-invalid' : ''}`;
        card.dataset.row = String(rowIndex);

        const head = document.createElement('div');
        head.className = 'fm-item__head';
        const name = document.createElement('span');
        name.className = 'fm-item__name';
        name.id = `FmItem-${this.dataset.sectionId}-${rowIndex}`;
        name.textContent = multiple ? fill(s.itemN, { n: rowIndex + 1 }) : s.itemSingle;
        const summary = document.createElement('span');
        summary.className = 'fm-item__summary';
        summary.textContent = row.map((value) => value ?? s.notSelected).join(' · ');
        head.append(name, summary);
        card.append(head);

        options.forEach((/** @type {any} */ option, /** @type {number} */ optionIndex) => {
          if (option.values.length <= 1 && !option.isColor) return;
          const group = document.createElement('div');
          group.className = 'fm-item__option';
          const groupLabel = document.createElement('span');
          groupLabel.className = 'fm-item__option-name';
          groupLabel.id = `${name.id}-o${optionIndex}`;
          groupLabel.textContent = option.name;
          const list = document.createElement('div');
          list.className = option.isColor ? 'fm-item__swatches' : 'fm-item__buttons';
          list.setAttribute('role', 'radiogroup');
          list.setAttribute('aria-labelledby', `${name.id} ${groupLabel.id}`);

          const selectedValue = row[optionIndex];
          let firstFocusable = -1;
          option.values.forEach((/** @type {any} */ value, /** @type {number} */ valueIndex) => {
            const state = this.#valueState(row, optionIndex, value.name);
            const disabled = !state.exists || !state.available;
            if (!disabled && firstFocusable < 0) firstFocusable = valueIndex;
          });

          option.values.forEach((/** @type {any} */ value, /** @type {number} */ valueIndex) => {
            const state = this.#valueState(row, optionIndex, value.name);
            const disabled = !state.exists || !state.available;
            const checked = selectedValue === value.name;
            const button = document.createElement('button');
            button.type = 'button';
            const useThumb = option.isColor && this.#data.swatchStyle === 'image' && Boolean(value.thumb);
            button.className = option.isColor ? `fm-swatch${useThumb ? ' fm-swatch--image' : ''}` : 'fm-opt';
            button.setAttribute('role', 'radio');
            button.setAttribute('aria-checked', String(checked));
            if (disabled) button.setAttribute('aria-disabled', 'true');
            const isTabStop = checked || (selectedValue === null && valueIndex === firstFocusable);
            button.tabIndex = isTabStop ? 0 : -1;
            button.dataset.row = String(rowIndex);
            button.dataset.option = String(optionIndex);
            button.dataset.value = value.name;
            button.dataset.focusKey = `${rowIndex}:${optionIndex}:${valueIndex}`;
            const stateText = disabled ? ` — ${state.exists ? s.soldOut : s.unavailable}` : '';
            if (option.isColor) {
              const dot = document.createElement('span');
              dot.className = 'fm-swatch__dot';
              if (useThumb) {
                const img = document.createElement('img');
                img.className = 'fm-swatch__thumb';
                img.src = value.thumb;
                img.alt = '';
                img.loading = 'lazy';
                img.width = 44;
                img.height = 44;
                dot.append(img);
              } else if (value.swatch) {
                dot.style.background = value.swatch;
              }
              button.append(dot);
              button.setAttribute('aria-label', `${value.name}${stateText}`);
              button.title = value.name;
            } else {
              button.textContent = value.name;
              if (stateText) button.setAttribute('aria-label', `${value.name}${stateText}`);
            }
            list.append(button);
          });

          group.append(groupLabel, list);
          card.append(group);
        });

        if (invalid) {
          const warn = document.createElement('p');
          warn.className = 'fm-item__warn';
          warn.id = `${name.id}-warn`;
          warn.textContent = s.warnRow;
          card.append(warn);
        }
        return card;
      })
    );

    if (focusKey) {
      /** @type {HTMLElement | null} */ (container.querySelector(`[data-focus-key="${focusKey}"]`))?.focus();
    }
  }

  #totals() {
    const data = this.#data;
    let sum = 0;
    let was = 0;
    let complete = true;
    let unavailable = false;
    for (const row of this.#rows) {
      const variant = this.#findVariant(row);
      if (!variant) complete = false;
      if (variant && !variant.available) unavailable = true;
      const price = variant ? variant.price : data.basePrice;
      const compareAt = variant ? variant.compareAt : 0;
      sum += price * this.#multiplier;
      was += Math.max(price, compareAt || 0) * this.#multiplier;
    }
    const discount = this.#tier.discount || 0;
    const total = Math.round((sum * (100 - discount)) / 100);
    return { sum, was, total, discount, complete, unavailable };
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.#moneyFormat, this.#data.currency || 'TRY');
  }

  #firstMissingOption() {
    for (const row of this.#rows) {
      const index = row.findIndex((value) => value === null);
      if (index >= 0) return this.#data.options[index]?.name || '';
    }
    return '';
  }

  #renderTotals() {
    const s = this.#data.strings;
    const { was, total, discount, complete, unavailable } = this.#totals();

    const price = this.querySelector('[data-price]');
    if (price) price.textContent = this.#money(total);
    const compare = /** @type {HTMLElement | null} */ (this.querySelector('[data-compare]'));
    if (compare) {
      compare.hidden = !(was > total);
      compare.textContent = this.#money(was);
    }
    const save = /** @type {HTMLElement | null} */ (this.querySelector('[data-save]'));
    if (save) {
      if (discount > 0) {
        save.hidden = false;
        save.textContent = fill(s.save, { percent: discount, amount: this.#money(was - total) });
      } else if (was > total) {
        save.hidden = false;
        save.textContent = fill(s.saveAmount, { amount: this.#money(was - total) });
      } else {
        save.hidden = true;
      }
    }
    const note = /** @type {HTMLElement | null} */ (this.querySelector('[data-tier-note]'));
    if (note) note.hidden = !(discount > 0);

    const submit = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-submit]'));
    const label = this.querySelector('[data-submit-label]');
    let text = fill(s.addToCartTotal, { price: this.#money(total) });
    let blocked = false;
    if (!complete) {
      text = fill(s.selectOptions, { option: this.#firstMissingOption() });
      blocked = true;
    } else if (unavailable) {
      text = s.soldOut;
    }
    if (submit && label && !this.#busy) {
      label.textContent = text;
      submit.disabled = complete && unavailable;
      if (blocked) submit.setAttribute('aria-disabled', 'true');
      else submit.removeAttribute('aria-disabled');
    }

    const stickyLabel = this.querySelector('[data-sticky-label]');
    if (stickyLabel) {
      const quantityText = fill(s.stickyQty, { count: this.#rows.length * this.#multiplier });
      const sizeIndex = this.#data.options.length > 1 ? this.#data.options.length - 1 : 0;
      const picks = this.#rows.map((row) => row[sizeIndex] ?? '?').join(', ');
      stickyLabel.textContent = this.#data.hasOnlyDefaultVariant ? quantityText : `${quantityText} · ${picks}`;
    }
    const stickyPrice = this.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = this.#money(total);
    const stickyButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-sticky-submit]'));
    if (stickyButton && this.hasAttribute('data-waitlist')) {
      stickyButton.textContent = s.waitlistCta || stickyButton.textContent;
      stickyButton.disabled = false;
    } else if (stickyButton && !this.#busy) {
      stickyButton.textContent = !complete ? fill(s.selectOptions, { option: this.#firstMissingOption() }) : unavailable ? s.soldOut : s.addToCart;
      stickyButton.disabled = complete && unavailable;
    }
  }

  /* ------------------------------------------------------------- quantity */

  /**
   * Plain quantity stepper, rendered only when the template has no quantity tiers.
   * @param {AbortSignal} signal
   */
  #setupQuantity(signal) {
    const wrap = /** @type {HTMLElement | null} */ (this.querySelector('[data-qty]'));
    const input = /** @type {HTMLInputElement | null} */ (wrap?.querySelector('[data-qty-input]') || null);
    if (!wrap || !input) return;
    const max = Math.max(1, Number(input.max) || 99);
    const minus = /** @type {HTMLButtonElement | null} */ (wrap.querySelector('[data-qty-step="-1"]'));
    const plus = /** @type {HTMLButtonElement | null} */ (wrap.querySelector('[data-qty-step="1"]'));
    /** @param {number} value */
    const set = (value) => {
      const next = Math.min(max, Math.max(1, Math.round(Number(value) || 1)));
      this.#multiplier = next;
      input.value = String(next);
      if (minus) minus.disabled = next <= 1;
      if (plus) plus.disabled = next >= max;
      this.#renderTotals();
    };
    wrap.addEventListener(
      'click',
      (event) => {
        const button = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest('[data-qty-step]'));
        if (!button) return;
        set(this.#multiplier + Number(button.dataset.qtyStep));
      },
      { signal }
    );
    input.addEventListener('change', () => set(Number(input.value)), { signal });
    set(1);
  }

  /* ------------------------------------------------------------ add to cart */

  /** @param {AbortSignal} signal */
  #setupForm(signal) {
    if (this.hasAttribute('data-waitlist')) this.#setupWaitlist(signal);
    const form = this.querySelector('[data-form]');
    form?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this.#addToCart();
      },
      { signal }
    );

    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const tierButton = /** @type {HTMLElement | null} */ (target.closest('.fm-tier'));
        if (tierButton && this.contains(tierButton)) {
          this.#selectTier(Number(tierButton.dataset.tier));
          return;
        }
        const optionButton = /** @type {HTMLElement | null} */ (target.closest('.fm-swatch, .fm-opt'));
        if (optionButton && optionButton.dataset.value !== undefined) {
          if (optionButton.getAttribute('aria-disabled') === 'true') return;
          this.#select(Number(optionButton.dataset.row), Number(optionButton.dataset.option), optionButton.dataset.value);
          return;
        }
        if (target.closest('[data-sticky-submit]')) {
          if (this.hasAttribute('data-waitlist')) this.#focusWaitlist();
          else this.#addToCart();
        }
      },
      { signal }
    );

    this.addEventListener('keydown', this.#onRadioKeydown, { signal });
  }

  /** @param {KeyboardEvent} event */
  #onRadioKeydown = (event) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const current = /** @type {HTMLElement} */ (event.target);
    if (current.getAttribute('role') !== 'radio') return;
    const group = current.closest('[role="radiogroup"]');
    if (!group) return;
    const radios = /** @type {HTMLElement[]} */ (
      [...group.querySelectorAll('[role="radio"]')].filter((el) => el.getAttribute('aria-disabled') !== 'true')
    );
    if (!radios.length) return;
    event.preventDefault();
    let index = radios.indexOf(current);
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = radios.length - 1;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') index = (index + 1) % radios.length;
    else index = (index - 1 + radios.length) % radios.length;
    radios[index]?.click();
  };

  async #refreshCart() {
    /** @type {any} */
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
    }
    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    const response = await fetch(`${cartUrl}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
    return response.json();
  }

  /** @param {string} message */
  #showError(message) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-error]'));
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  /** @param {string} message */
  #announce(message) {
    const live = this.querySelector('[data-live]');
    if (live) live.textContent = message;
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    const submit = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-submit]'));
    const label = this.querySelector('[data-submit-label]');
    const sticky = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-sticky-submit]'));
    if (busy) {
      submit?.setAttribute('aria-busy', 'true');
      if (label) label.textContent = this.#data.strings.adding;
      if (sticky) sticky.textContent = this.#data.strings.adding;
    } else {
      submit?.removeAttribute('aria-busy');
      this.#renderTotals();
    }
  }

  async #addToCart() {
    if (this.#busy) return;
    const s = this.#data.strings;
    this.#showError('');
    const { complete, unavailable } = this.#totals();

    if (!complete) {
      this.#tried = true;
      this.#render();
      this.#announce(s.warnAll);
      const invalid = /** @type {HTMLElement | null} */ (this.querySelector('.fm-item.is-invalid'));
      invalid?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
      const firstOpen = /** @type {HTMLElement | null} */ (
        invalid?.querySelector('[role="radiogroup"] [tabindex="0"][aria-checked="false"]')
      );
      firstOpen?.focus({ preventScroll: true });
      return;
    }
    if (unavailable) return;

    /** @type {Map<number, number>} */
    const counts = new Map();
    for (const row of this.#rows) {
      const variant = this.#findVariant(row);
      if (variant) counts.set(variant.id, (counts.get(variant.id) || 0) + this.#multiplier);
    }
    const items = [...counts].map(([id, quantity]) => ({ id, quantity }));
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    if (!items.length) return;

    this.#setBusy(true);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();

      if (result.status) {
        const message = result.description || result.message || s.error;
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        try {
          const cart = await this.#refreshCart();
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'fm-product', itemCount, productId: this.dataset.productId },
          });
        } catch (refreshError) {
          deferred.reject(refreshError);
        }
        this.#showError(message);
        this.#announce(message);
        return;
      }

      const cart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'fm-product',
          sourceId: this.dataset.sectionId,
          itemCount,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
      this.#announce(s.added);
      this.#openCartIfClosed();
    } catch (error) {
      if (/** @type {any} */ (error)?.name !== 'AbortError') console.error('[fm-product] Add to cart failed', error);
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      this.#showError(s.error);
      this.#announce(s.error);
    } finally {
      this.#setBusy(false);
    }
  }

  #openCartIfClosed() {
    window.setTimeout(() => {
      /** @type {any} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer) {
        if (!drawer.isOpen && !drawer.hasAttribute('open')) {
          if (window.Shopify?.actions?.openCart) window.Shopify.actions.openCart();
          else drawer.open?.();
        }
        return;
      }
      window.location.href = window.Theme?.routes?.cart_url || '/cart';
    }, 350);
  }

  /* ------------------------------------------------------------- waitlist */

  /**
   * Waitlist ("ön kayıt") mode: the buy form is a native Shopify customer form. Before it is
   * submitted, the current option picks are appended to the customer tags so interest can be
   * measured per product, color, size and quantity in the admin. Picks are optional.
   * @param {AbortSignal} signal
   */
  #setupWaitlist(signal) {
    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('.fm-wait__form'));
    const tagsInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-wait-tags]'));
    const baseTags = tagsInput?.value || '';
    form?.addEventListener(
      'submit',
      () => {
        if (!tagsInput) return;
        const clean = (/** @type {string} */ value) => String(value).replace(/,/g, ' ').trim();
        const tags = new Set(baseTags.split(',').map((tag) => tag.trim()).filter(Boolean));
        const options = this.#data.options || [];
        for (const row of this.#rows) {
          row.forEach((value, index) => {
            if (value !== null && value !== undefined) tags.add(`${clean(options[index]?.name || '')}: ${clean(value)}`);
          });
        }
        tags.add(`Adet: ${this.#rows.length * this.#multiplier}`);
        tagsInput.value = [...tags].join(', ');
      },
      { signal }
    );

    const success = /** @type {HTMLElement | null} */ (this.querySelector('[data-wait-success]'));
    if (success) {
      requestAnimationFrame(() => {
        success.scrollIntoView({ behavior: 'auto', block: 'center' });
        success.focus({ preventScroll: true });
      });
    }
  }

  #focusWaitlist() {
    const email = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-wait-email]'));
    const target = email || this.querySelector('[data-wait-success]');
    if (!target) return;
    target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    if (email) email.focus({ preventScroll: true });
  }

  /* --------------------------------------------------------------- sticky */

  /** @param {AbortSignal} signal */
  #setupSticky(signal) {
    const sticky = /** @type {HTMLElement | null} */ (this.querySelector('[data-sticky]'));
    const submit = this.querySelector('[data-submit]');
    if (!sticky || !submit || !('IntersectionObserver' in window)) return;
    const footer = document.querySelector('footer');

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === submit) {
          this.#visibility.submit = entry.isIntersecting;
          this.#visibility.submitAbove = entry.boundingClientRect.top < 0;
        } else if (entry.target === footer) {
          this.#visibility.footer = entry.isIntersecting;
        }
      }
      const show = !this.#visibility.submit && this.#visibility.submitAbove && !this.#visibility.footer;
      sticky.classList.toggle('is-visible', show);
      sticky.toggleAttribute('inert', !show);
      sticky.setAttribute('aria-hidden', String(!show));
    });
    this.#observer.observe(submit);
    if (footer) this.#observer.observe(footer);
    signal.addEventListener('abort', () => this.#observer?.disconnect());
  }

  /* -------------------------------------------------------------- gallery */

  /** @param {AbortSignal} signal */
  #setupGallery(signal) {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    if (!track) return;
    track.style.position = 'relative';

    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.#scrollFrame);
        this.#scrollFrame = requestAnimationFrame(() => this.#syncSlideFromScroll());
      },
      { passive: true, signal }
    );
    track.addEventListener(
      'keydown',
      (event) => {
        if (event.target !== track) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.#step(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.#step(-1);
        }
      },
      { signal }
    );

    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const thumb = /** @type {HTMLElement | null} */ (target.closest('[data-goto]'));
        if (thumb) {
          this.#goTo(this.#visibleSlides().findIndex((slide) => slide.dataset.index === thumb.dataset.goto));
          return;
        }
        if (target.closest('[data-prev]')) this.#step(-1);
        else if (target.closest('[data-next]')) this.#step(1);
        const zoom = /** @type {HTMLElement | null} */ (target.closest('[data-zoom]'));
        if (zoom) this.#openLightbox(Number(zoom.dataset.zoom));
      },
      { signal }
    );

    const lightbox = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-lightbox]'));
    if (lightbox) {
      lightbox.addEventListener(
        'click',
        (event) => {
          const target = /** @type {HTMLElement} */ (event.target);
          if (target.closest('[data-lightbox-close]')) lightbox.close();
          else if (target.closest('[data-lightbox-prev]')) this.#stepLightbox(-1);
          else if (target.closest('[data-lightbox-next]')) this.#stepLightbox(1);
        },
        { signal }
      );
      lightbox.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowRight') this.#stepLightbox(1);
          else if (event.key === 'ArrowLeft') this.#stepLightbox(-1);
        },
        { signal }
      );
      const lightboxTrack = lightbox.querySelector('[data-lightbox-track]');
      lightboxTrack?.addEventListener('scroll', () => this.#updateLightboxCount(), { passive: true, signal });
    }
  }

  #visibleSlides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-slide]')].filter((el) => !(/** @type {HTMLElement} */ (el).hidden)));
  }

  #syncSlideFromScroll() {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    if (!track || !track.clientWidth) return;
    const index = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
    this.#markSlide(index);
  }

  /** @param {number} index */
  #markSlide(index) {
    const slides = this.#visibleSlides();
    if (!slides.length) return;
    this.#slideIndex = Math.max(0, Math.min(index, slides.length - 1));
    const activeIndex = slides[this.#slideIndex]?.dataset.index;
    this.querySelectorAll('[data-goto]').forEach((thumb) => {
      const isActive = /** @type {HTMLElement} */ (thumb).dataset.goto === activeIndex;
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
        const list = /** @type {HTMLElement | null} */ (thumb.parentElement);
        const el = /** @type {HTMLElement} */ (thumb);
        if (list && list.scrollHeight > list.clientHeight) {
          const top = el.offsetTop;
          if (top < list.scrollTop || top + el.offsetHeight > list.scrollTop + list.clientHeight) {
            list.scrollTo({ top: Math.max(0, top - 8), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          }
        }
      }
      else thumb.removeAttribute('aria-current');
    });
    this.querySelectorAll('[data-dot]').forEach((dot) => {
      dot.classList.toggle('is-active', /** @type {HTMLElement} */ (dot).dataset.dot === activeIndex);
    });
  }

  /** @param {number} index */
  #goTo(index) {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    const slides = this.#visibleSlides();
    if (!track || !slides.length || index < 0) return;
    const bounded = (index + slides.length) % slides.length;
    const slide = slides[bounded];
    if (!slide) return;
    track.scrollTo({ left: slide.offsetLeft, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    this.#markSlide(bounded);
  }

  /** @param {number} delta */
  #step(delta) {
    this.#goTo(this.#slideIndex + delta);
  }

  /** @param {number} mediaId */
  #goToMedia(mediaId) {
    const index = this.#visibleSlides().findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.#goTo(index);
  }

  /** @param {boolean} resetPosition */
  #applyMediaFilter(resetPosition) {
    if (this.dataset.filterColor !== 'true' || this.#colorOptionIndex < 0) return;
    const option = this.#data.options[this.#colorOptionIndex];
    const handles = new Set(option.values.map((/** @type {any} */ value) => value.handle));
    const slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-slide]')]);
    const tagged = slides.some((slide) => handles.has(slide.dataset.color || ''));
    if (!tagged) return;
    const selectedName = this.#rows[0]?.[this.#colorOptionIndex];
    const selected = option.values.find((/** @type {any} */ value) => value.name === selectedName);
    const selectedHandle = selected?.handle || '';
    if (this.dataset.colorMode === 'reorder') {
      this.#reorderMedia(slides, selectedHandle, resetPosition);
      return;
    }
    const visibleIds = new Set();
    for (const slide of slides) {
      const color = slide.dataset.color || '';
      const show = !handles.has(color) || color === selectedHandle || !selectedHandle;
      slide.hidden = !show;
      if (show) visibleIds.add(slide.dataset.index);
    }
    this.querySelectorAll('[data-goto]').forEach((thumb) => {
      /** @type {HTMLElement} */ (thumb).hidden = !visibleIds.has(/** @type {HTMLElement} */ (thumb).dataset.goto);
    });
    this.querySelectorAll('[data-dot]').forEach((dot) => {
      /** @type {HTMLElement} */ (dot).hidden = !visibleIds.has(/** @type {HTMLElement} */ (dot).dataset.dot);
    });
    // The first visible image of the selected color is the page's main image: load it first.
    const lead = /** @type {HTMLImageElement | null | undefined} */ (
      slides.find((slide) => !slide.hidden)?.querySelector('img')
    );
    if (lead) {
      lead.loading = 'eager';
      lead.fetchPriority = 'high';
    }
    if (resetPosition) {
      const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
      track?.scrollTo({ left: 0 });
    }
    this.#markSlide(resetPosition ? 0 : this.#slideIndex);
  }

  /**
   * Shows every image, with the selected color's images moved to the front (gallery, thumbnails,
   * dots and lightbox keep the same order). Other colors follow in their original order.
   * @param {HTMLElement[]} slides
   * @param {string} selectedHandle
   * @param {boolean} resetPosition
   */
  #reorderMedia(slides, selectedHandle, resetPosition) {
    const colorByIndex = new Map(slides.map((slide) => [slide.dataset.index || '', slide.dataset.color || '']));
    /** @param {string | undefined} index */
    const rank = (index) => (selectedHandle && colorByIndex.get(index || '') === selectedHandle ? 0 : 1);
    /**
     * @param {Element | null} parent
     * @param {(el: HTMLElement) => string | undefined} key
     */
    const sortChildren = (parent, key) => {
      if (!parent) return;
      const items = /** @type {HTMLElement[]} */ ([...parent.children]);
      items
        .map((el, position) => ({ el, position, rank: rank(key(el)), original: Number(key(el)) }))
        .sort((a, b) => a.rank - b.rank || a.original - b.original || a.position - b.position)
        .forEach(({ el }) => {
          el.hidden = false;
          parent.append(el);
        });
    };
    const track = this.querySelector('[data-track]');
    sortChildren(track, (el) => el.dataset.index);
    sortChildren(this.querySelector('.fm-gallery__thumbs'), (el) => el.dataset.goto);
    sortChildren(this.querySelector('.fm-gallery__dots'), (el) => el.dataset.dot);
    sortChildren(this.querySelector('[data-lightbox-track]'), (el) => el.dataset.lightboxSlide);
    // Load the new lead image first.
    const lead = /** @type {HTMLImageElement | null | undefined} */ (track?.querySelector('[data-slide] img'));
    if (lead) {
      lead.loading = 'eager';
      lead.fetchPriority = 'high';
    }
    if (resetPosition || selectedHandle) {
      /** @type {HTMLElement | null} */ (track)?.scrollTo({ left: 0 });
      this.querySelector('.fm-gallery__thumbs')?.scrollTo?.({ top: 0 });
      this.#markSlide(0);
    } else {
      this.#markSlide(this.#slideIndex);
    }
  }

  /** @param {number} mediaIndex */
  #openLightbox(mediaIndex) {
    const lightbox = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-lightbox]'));
    if (!lightbox) return;
    // Mirror the gallery's color filter so the lightbox only pages through the selected color.
    const shown = new Set(
      [...this.querySelectorAll('[data-slide]')]
        .filter((slide) => !(/** @type {HTMLElement} */ (slide).hidden))
        .map((slide) => /** @type {HTMLElement} */ (slide).dataset.index)
    );
    lightbox.querySelectorAll('[data-lightbox-slide]').forEach((figure) => {
      /** @type {HTMLElement} */ (figure).hidden = !shown.has(/** @type {HTMLElement} */ (figure).dataset.lightboxSlide);
    });
    lightbox.showModal();
    const slide = /** @type {HTMLElement | null} */ (lightbox.querySelector(`[data-lightbox-slide="${mediaIndex}"]`));
    const track = /** @type {HTMLElement | null} */ (lightbox.querySelector('[data-lightbox-track]'));
    if (slide && track) {
      track.style.position = 'relative';
      track.scrollTo({ left: slide.offsetLeft });
    }
    this.#updateLightboxCount();
    /** @type {HTMLElement | null} */ (lightbox.querySelector('[data-lightbox-close]'))?.focus();
  }

  /** @param {number} delta */
  #stepLightbox(delta) {
    const lightbox = this.querySelector('[data-lightbox]');
    const track = /** @type {HTMLElement | null} */ (lightbox?.querySelector('[data-lightbox-track]'));
    if (!track) return;
    const slides = [...track.children].filter((child) => !(/** @type {HTMLElement} */ (child).hidden)).length || 1;
    const current = Math.round(Math.abs(track.scrollLeft) / (track.clientWidth || 1));
    const next = (current + delta + slides) % slides;
    track.scrollTo({ left: next * track.clientWidth, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  #updateLightboxCount() {
    const lightbox = this.querySelector('[data-lightbox]');
    const track = /** @type {HTMLElement | null} */ (lightbox?.querySelector('[data-lightbox-track]'));
    const count = lightbox?.querySelector('[data-lightbox-count]');
    if (!track || !count) return;
    const total = [...track.children].filter((child) => !(/** @type {HTMLElement} */ (child).hidden)).length;
    const current = Math.round(Math.abs(track.scrollLeft) / (track.clientWidth || 1)) + 1;
    count.textContent = fill(this.#data.strings.imageCount, { index: Math.min(current, total), count: total });
  }

  /* ----------------------------------------------------------- size guide */

  /** @param {AbortSignal} signal */
  #setupSizeGuide(signal) {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-size-guide]'));
    if (!dialog) return;

    this.addEventListener(
      'click',
      (event) => {
        if (/** @type {HTMLElement} */ (event.target).closest('[data-open-size-guide]')) this.#openSizeGuide();
      },
      { signal }
    );
    dialog.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (target === dialog || target.closest('[data-size-close]')) dialog.close();
        else if (target.closest('[data-size-apply]')) this.#applyRecommendedSize();
      },
      { signal }
    );

    const rules = (this.#data.sizeRules || []).filter(
      (/** @type {any} */ rule) => rule.minH !== null && rule.maxH !== null && rule.minW !== null && rule.maxW !== null
    );
    const finder = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-size-finder]'));
    if (!finder || !rules.length) return;

    const sizeValues = new Set(rules.map((/** @type {any} */ rule) => rule.size));
    this.#sizeOptionIndex = this.#data.options.findIndex((/** @type {any} */ option) =>
      option.values.some((/** @type {any} */ value) => sizeValues.has(value.name))
    );
    if (this.#sizeOptionIndex < 0) return;

    finder.hidden = false;
    const height = /** @type {HTMLInputElement} */ (finder.querySelector('[data-height]'));
    const weight = /** @type {HTMLInputElement} */ (finder.querySelector('[data-weight]'));
    const update = () => this.#updateRecommendation(rules, Number(height.value), Number(weight.value));
    height.addEventListener('input', update, { signal });
    weight.addEventListener('input', update, { signal });
    finder.addEventListener('change', update, { signal });
    update();
  }

  #openSizeGuide() {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-size-guide]'));
    if (!dialog || dialog.open) return;
    const apply = this.querySelector('[data-size-apply]');
    if (apply) apply.textContent = this.#rows.length > 1 ? this.#data.strings.sizeApplyAll : this.#data.strings.sizeApply;
    dialog.showModal();
  }

  /** @param {Event} event */
  #onExternalSizeGuide = (event) => {
    event.preventDefault?.();
    this.#openSizeGuide();
  };

  /**
   * FormFit: picks the size whose height/weight ranges (from the "Beden ölçüsü" metaobjects) are
   * closest to the input, then adjusts for belly shape and fit preference. Outside the chart range
   * nothing is recommended.
   * @param {any[]} rules
   * @param {number} height
   * @param {number} weight
   */
  #updateRecommendation(rules, height, weight) {
    const s = this.#data.strings;
    const dialog = this.querySelector('[data-size-guide]');
    if (!dialog) return;
    const heightOut = dialog.querySelector('[data-height-out]');
    const weightOut = dialog.querySelector('[data-weight-out]');
    if (heightOut) heightOut.textContent = `${height} ${s.cm}`;
    if (weightOut) weightOut.textContent = `${weight} ${s.kg}`;

    const checked = (/** @type {string} */ selector, fallback = 0) => {
      const input = /** @type {HTMLInputElement | null} */ (dialog.querySelector(`${selector} input:checked`));
      return input ? Number(input.value) : fallback;
    };
    const belly = checked('[data-belly]', 1);
    const relaxed = checked('[data-fit]', 0) === 1;

    const ordered = [...rules].sort((a, b) => a.minW - b.minW);
    const last = ordered.length - 1;
    const outside =
      weight < ordered[0].minW - 8 ||
      weight > ordered[last].maxW + 8 ||
      height < ordered[0].minH - 8 ||
      height > ordered[last].maxH + 8;

    let base = 0;
    let bestScore = Infinity;
    ordered.forEach((rule, index) => {
      const dw = weight < rule.minW ? rule.minW - weight : weight > rule.maxW ? weight - rule.maxW : 0;
      const dh = height < rule.minH ? rule.minH - height : height > rule.maxH ? height - rule.maxH : 0;
      const score = dw + dh * 0.5;
      if (score < bestScore - 0.001) {
        bestScore = score;
        base = index;
      }
    });

    const rule = ordered[base];
    const position = (weight - rule.minW) / Math.max(1, rule.maxW - rule.minW);
    const notes = [];
    let index = base;
    if (belly === 2 && position >= 0.5 && index < last) {
      index += 1;
      notes.push(s.sizeNoteBelly);
    }
    if (relaxed && index < last) {
      index += 1;
      notes.push(s.sizeNoteFit);
    }
    if (index > base && height < ordered[index].minH - 6) {
      index -= 1;
      notes.pop();
      notes.push(s.sizeNoteShort);
    }

    let alt = '';
    if (index === base) {
      if (position >= 0.75 && base < last) alt = fill(s.sizeAltUp, { size: ordered[base + 1].size });
      else if (position <= 0.2 && base > 0) alt = fill(s.sizeAltDown, { size: ordered[base - 1].size });
    }

    const size = outside ? '' : ordered[index].size;
    this.#recommended = size;
    const result = dialog.querySelector('[data-size-result]');
    const note = dialog.querySelector('[data-size-note]');
    const altEl = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-size-alt]'));
    const apply = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-size-apply]'));
    if (result) result.textContent = size || '—';
    if (note) note.textContent = size ? [fill(s.sizeBasis, { height, weight }), ...notes].join(' ') : s.sizeNoMatch;
    if (altEl) {
      altEl.textContent = alt;
      altEl.hidden = !size || !alt;
    }
    if (apply) apply.disabled = !size;
  }

  #applyRecommendedSize() {
    const size = this.#recommended;
    const optionIndex = this.#sizeOptionIndex;
    if (!size || optionIndex < 0) return;
    this.#rows.forEach((row) => {
      if (this.#valueState(row, optionIndex, size).available) row[optionIndex] = size;
    });
    this.#tried = false;
    this.#render();
    this.#syncUrl();
    /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-size-guide]'))?.close();
    this.#announce(fill(this.#data.strings.sizeApplied, { size }));
  }
}

if (!customElements.get('fm-product')) {
  customElements.define('fm-product', FmProduct);
}
